import { useEffect } from 'react';
import type { CollectionBlocksField, CollectionRef } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { useBuilderStore } from '../../state/builderStore';
import { bt } from '../../i18n';

/**
 * The checklist behind « Collections choisies » (22/09/2026): every ACTIVE collection of the store, ticked
 * or not. A tick ADDS a `collection` block to the owning section with the collection as its `ref`; an
 * untick REMOVES that block. The blocks stay the stored truth — the storefront and the backend resolve
 * them exactly as before — this control is only the door the seller was looking for: « normalement on
 * doit avoir un select où je peux sélectionner les collections que je veux afficher ».
 */
export function CollectionBlocksControl({
  field,
  sectionId,
}: {
  field: CollectionBlocksField;
  sectionId: string | null;
}) {
  const section = useBuilderStore((s) => (sectionId ? s.sectionsById[sectionId] : undefined));
  const catalog = useBuilderStore((s) => s.collectionsCatalog);
  const catalogState = useBuilderStore((s) => s.collectionsCatalogState);
  const loadCollectionsCatalog = useBuilderStore((s) => s.loadCollectionsCatalog);
  const addBlock = useBuilderStore((s) => s.addBlock);
  const removeBlock = useBuilderStore((s) => s.removeBlock);
  const updateBlockSettings = useBuilderStore((s) => s.updateBlockSettings);

  useEffect(() => {
    void loadCollectionsCatalog();
  }, [loadCollectionsCatalog]);

  // collection id → the block that carries it (the first one, should a duplicate ever exist).
  const blockOf = new Map<string, string>();
  for (const b of section?.blocks ?? []) {
    if (b.type !== field.blockType || b.disabled) continue;
    const ref = (b.settings as { ref?: CollectionRef }).ref;
    if (ref?.id && !blockOf.has(ref.id)) blockOf.set(ref.id, b.id);
  }

  function toggle(collectionId: string) {
    if (!sectionId) return;
    const existing = blockOf.get(collectionId);
    if (existing) {
      removeBlock(sectionId, existing);
      return;
    }
    addBlock(sectionId, field.blockType);
    // `addBlock` appends; the new block is the last one of its type with no ref yet.
    const fresh = [...(useBuilderStore.getState().sectionsById[sectionId]?.blocks ?? [])]
      .reverse()
      .find((b) => b.type === field.blockType && !(b.settings as { ref?: CollectionRef }).ref?.id);
    if (fresh) {
      updateBlockSettings(sectionId, fresh.id, { ref: { kind: 'collection', id: collectionId } });
    }
    // `addBlock` selects the block it creates, which would swap this form for the block's own and hide
    // the checklist under the seller's finger. She is editing the SECTION: keep it selected.
    useBuilderStore.getState().selectSection(sectionId);
  }

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span">
      <ul className="bld-pagesel__list bld-collpick" role="group" aria-label={field.label}>
        {catalogState === 'loading' && catalog.length === 0 && (
          <li className="bld-pagesel__empty">
            {bt('chrome.coll.loading', 'Loading your collections…')}
          </li>
        )}
        {catalogState === 'error' && (
          <li className="bld-pagesel__empty">
            {bt('chrome.coll.list_failed', 'Couldn’t load your collections.')}{' '}
            <button
              type="button"
              className="bld-pagesel__retry"
              onClick={() => void loadCollectionsCatalog()}
            >
              {bt('chrome.common.retry', 'Retry')}
            </button>
          </li>
        )}
        {catalogState === 'loaded' && catalog.length === 0 && (
          <li className="bld-pagesel__empty">{bt('chrome.coll.none', 'No collections yet')}</li>
        )}
        {catalog.map((c) => {
          const checked = blockOf.has(c.id);
          return (
            <li key={c.id} role="none">
              <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                className="bld-pagesel__item bld-pagesel__item--product"
                onClick={() => toggle(c.id)}
              >
                {c.coverImage?.url ? (
                  <img
                    className="bld-pagesel__thumb"
                    src={c.coverImage.url}
                    alt=""
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    className="bld-pagesel__thumb bld-pagesel__thumb--placeholder"
                    aria-hidden="true"
                  >
                    {(c.name.trim().charAt(0) || 'C').toUpperCase()}
                  </span>
                )}
                <span className="bld-pagesel__itemlabel">{c.name}</span>
                {checked && <span className="bld-pagesel__nowdot" aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ul>
    </FieldRow>
  );
}
